import { useEffect } from "react";
import { useParams, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { languages } from "@/i18n";
import { validLanguages, nonEnglishLanguages, rtlLanguages } from "@/lib/seo";

interface LanguageWrapperProps {
  children: React.ReactNode;
}

/**
 * Wrapper for root English routes (no /en/ prefix)
 * Sets English as the active language
 */
export function RootEnglishWrapper({ children }: LanguageWrapperProps) {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (i18n.language !== "en") {
      i18n.changeLanguage("en");
    }
    localStorage.setItem("vx-language", "en");
    document.documentElement.dir = "ltr";
    document.documentElement.lang = "en";
  }, [i18n]);

  return <>{children}</>;
}

/**
 * Wrapper for non-English language routes (/:lang prefix)
 * Validates language and sets it as active
 */
export function LanguageWrapper({ children }: LanguageWrapperProps) {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (lang && nonEnglishLanguages.has(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
        localStorage.setItem("vx-language", lang);
      }

      // Set document direction for RTL languages
      document.documentElement.dir = rtlLanguages.has(lang) ? "rtl" : "ltr";
      document.documentElement.lang = lang;
    }
  }, [lang, i18n]);

  // If lang is 'en', redirect to root (English should be at /)
  if (lang === "en") {
    const pathWithoutLang = location.pathname.replace(/^\/en\/?/, "/");
    return (
      <Navigate to={pathWithoutLang + location.search + location.hash} replace />
    );
  }

  // If lang param is invalid or not a non-English language, redirect
  if (!lang || !nonEnglishLanguages.has(lang)) {
    // Check if it might be a page path at root (English)
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Redirects /en/* paths to /* (root without prefix)
 */
export function EnglishRedirect() {
  const location = useLocation();

  // Remove /en prefix and redirect to root
  const newPath = location.pathname.replace(/^\/en\/?/, "/") || "/";

  return <Navigate to={newPath + location.search + location.hash} replace />;
}
